import { CheckedIcon, SearchIcon, UnCheckedIcon } from '@components/icon/svg';
import { BootstrapDialog } from '@components/shared/BootstrapDialog';
import Spinner from '@components/spinner/Spinner';
import { useAssignTagInTranscript } from '@hooks/postQueries';
import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { formatInTitleCase } from '@utils/constants';
import { useEffect, useState } from 'react';
import TagLibraryModal from '../details/components/TagLibraryModal';

const AssignTags = ({
  onClose,
  open,
  tags,
  id,
  transcriptId,
  speakerId,
  isFetchingTags,
  searchQuery,
  setSearchQuery,
  canEdit,
  assigned_tags = [],
}) => {
  const theme = useTheme();
  const { mutateAsync: assignTagInTranscript, isPending: isPendingProcess } =
    useAssignTagInTranscript();
  const [openTagLibraryModal, setOpenTagLibraryModal] = useState(false);
  const [selection, setSelection] = useState({});

  const toggleValue = (tagKey, value) => (e) => {
    setSelection((prev) => {
      const next = { ...prev };
      const tag = next[tagKey] ?? { checked: true, values: new Set() };
      const updatedValues = new Set(tag.values);

      if (e.target.checked) {
        updatedValues.add(value);
      } else {
        updatedValues.delete(value);
      }

      if (updatedValues.size === 0) {
        delete next[tagKey];
      } else {
        next[tagKey] = { checked: true, values: updatedValues };
      }

      return next;
    });
  };

  function getTagIdByName(tagName) {
    const tag = tags?.tags?.find((t) => t.tag_key === tagName);
    return tag ? tag.id : null;
  }

  function getValueIdByName(tagName, valueName) {
    const tag = tags?.tags?.find((t) => t.tag_key === tagName);
    if (!tag) return null;
    const value = tag.tag_values.find((v) => v.value === valueName);
    return value ? value.id : null;
  }

  const handleAssign = async () => {
    const tagsPayload = Object.entries(selection).flatMap(([tagKey, data]) =>
      [...data.values].map((valueKey) => ({
        tag_key_id: getTagIdByName(tagKey),
        tag_value_id: getValueIdByName(tagKey, valueKey),
      })),
    );

    try {
      await assignTagInTranscript({
        payload: { tags: tagsPayload },
        transcriptId,
        studyId: id,
        speakerId,
      });
      setSelection({});
      onClose();
    } catch (error) {
      console.error('Failed to assign tag. Please try again.', error?.detail || error);
    }
  };

  useEffect(() => {
    const initialSelection = {};
    (assigned_tags ?? []).forEach((tag) => {
      initialSelection[tag.tag_key] = {
        checked: true,
        values: new Set(tag.values.map((v) => v.value_key)),
      };
    });
    setSelection(initialSelection);
  }, [assigned_tags]);

  const handleCloseTagLibraryModal = () => {
    setOpenTagLibraryModal(false);
  };

  const selectedCount = Object.entries(selection)?.length ?? 0;
  const selectedValues = Object.values(selection).reduce(
    (sum, obj) => sum + (obj.values?.size ?? 0),
    0,
  );

  return (
    <>
      <BootstrapDialog
        open={open}
        onClose={onClose}
        aria-labelledby="tag-modal-title"
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Assign Tags
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add labels for easier sorting and filtering
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} sx={{ color: theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ p: 0 }}>
          {/* Search */}
          {tags?.tags?.length > 0 && (
            <Box sx={{ p: 2, pb: 1 }}>
              <TextField
                value={searchQuery}
                sx={{ background: '#fff' }}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tags..."
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={16} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {/* Tags List */}
          <Box
            sx={{
              maxHeight: 320,
              overflowY: 'auto',
              px: 2,
              py: 1,
            }}
          >
            {isFetchingTags ? (
              <Box display="flex" justifyContent="center" py={4}>
                <Spinner />
              </Box>
            ) : !tags || tags?.tags?.length <= 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={4}
              >
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No tags available yet.
                </Typography>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setOpenTagLibraryModal(true)}
                  sx={{ mt: 1, cursor: 'pointer' }}
                  disabled={!canEdit}
                >
                  Create your first tag
                </Link>
              </Box>
            ) : (
              tags.tags.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    backgroundColor:
                      selection[item.tag_key]?.values?.size > 0
                        ? alpha(theme.palette.primary.main, 0.04)
                        : 'transparent',
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{
                      mb: 0.75,
                      display: 'block',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {formatInTitleCase(item.tag_key)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(item.tag_values || []).map((raw) => {
                      const val = raw.value ?? raw;
                      const isSelected = selection[item.tag_key]?.values?.has(val) ?? false;
                      return (
                        <Chip
                          key={val}
                          size="small"
                          label={
                            <FormControlLabel
                              sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                              control={
                                <Checkbox
                                  size="small"
                                  checkedIcon={<CheckedIcon />}
                                  icon={<UnCheckedIcon />}
                                  checked={isSelected}
                                  onChange={toggleValue(item.tag_key, val)}
                                  sx={{ p: 0.5 }}
                                />
                              }
                              label={formatInTitleCase(val)}
                            />
                          }
                          sx={{
                            height: 28,
                            borderRadius: 1,
                            backgroundColor: isSelected
                              ? alpha(theme.palette.primary.main, 0.1)
                              : alpha(theme.palette.grey[500], 0.08),
                            border: isSelected
                              ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                              : '1px solid transparent',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? alpha(theme.palette.primary.main, 0.15)
                                : alpha(theme.palette.grey[500], 0.12),
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.grey[500], 0.02),
            }}
          >
            {tags?.tags?.length > 0 && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="caption" color="text.secondary">
                  {selectedCount > 0
                    ? `${selectedCount} tag${selectedCount > 1 ? 's' : ''} selected (${selectedValues} value${selectedValues > 1 ? 's' : ''})`
                    : 'No tags selected'}
                </Typography>
                <Link
                  component="button"
                  variant="caption"
                  onClick={() => setOpenTagLibraryModal(true)}
                  sx={{ cursor: 'pointer' }}
                  disabled={!canEdit}
                >
                  Don&apos;t see a tag? Manage tags
                </Link>
              </Box>
            )}

            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={onClose} size="small">
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={selectedValues === 0 || isPendingProcess}
                onClick={handleAssign}
                size="small"
              >
                {isPendingProcess ? 'Assigning...' : 'Assign Tags'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </BootstrapDialog>

      <TagLibraryModal
        open={openTagLibraryModal}
        onClose={handleCloseTagLibraryModal}
        studyId={id}
      />
    </>
  );
};

export default AssignTags;
